import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { getMyFlags } from '../api/me';

interface UserFlag {
  id: string;
  user_id: string;
  flag: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface UserFlagsState {
  flags: UserFlag[];
  isLoading: boolean;
}

// Several components mount this hook independently on the same page; share one
// request/result per user instead of firing a call per mount.
const flagsCache = new Map<string, Promise<UserFlag[]>>();

const fetchFlags = (userId: string): Promise<UserFlag[]> => {
  let cached = flagsCache.get(userId);
  if (!cached) {
    cached = getMyFlags()
      .then((data: any) => (data?.flags || []) as UserFlag[])
      .catch(() => {
        // Fail closed, but let a later mount retry after a transient error.
        flagsCache.delete(userId);
        return [];
      });
    flagsCache.set(userId, cached);
  }
  return cached;
};

export function useUserFlags() {
  const userId: string | undefined = useSelector(
    (state: RootState) => state.login?.user?.user?.id,
  );

  const [state, setState] = useState<UserFlagsState>({
    flags: [],
    isLoading: true,
  });

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    fetchFlags(userId).then((flags) => {
      if (!cancelled) setState({ flags, isLoading: false });
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return {
    flags: state.flags,
    isLoading: state.isLoading,
  };
}
